import React, { useEffect, useMemo, useState } from 'react';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Plus, Trash2, Save, Loader2, ArrowUp, ArrowDown,
  Workflow, ShieldCheck, Search, CheckCircle2,
  Upload, Download, FileSpreadsheet, AlertTriangle, X, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DOC_TYPE_LABEL = {
  expense_claim_sheet: 'Expense Claim Sheets',
  loan: 'Advances',
  local_travel_report: 'Local Conveyance Claims',
  outstation_claim: 'Outstation Claims',
  travel_request: 'Travel Requests',
};

const ROLE_LABEL = {
  manager: 'Reporting Manager / HOD',
  hr_admin: 'HR Admin',
  payroll_admin: 'Payroll Admin',
  finance_reviewer: 'Finance Reviewer',
  tenant_admin: 'Tenant Admin',
};

export default function ApprovalWorkflows() {
  const [meta, setMeta] = useState({
    doc_types: ['expense_claim_sheet', 'loan', 'local_travel_report', 'outstation_claim', 'travel_request'],
    approver_roles: ['manager', 'hr_admin', 'payroll_admin', 'finance_reviewer', 'tenant_admin'],
    departments: [],
    employees: [],
  });
  const [activeDocType, setActiveDocType] = useState('expense_claim_sheet');
  const [activeDept, setActiveDept] = useState('');
  const [workflows, setWorkflows] = useState([]);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);

  // Bulk Upload & Export States
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  useEffect(() => { loadMeta(); loadUsers(); }, []);
  useEffect(() => {
    if (meta.doc_types.length) {
      loadWorkflows();
    }
    /* eslint-disable-next-line */
  }, [activeDocType, activeDept, meta.doc_types.length]);

  const loadMeta = async () => {
    try {
      const m = await axios.get(`${API}/approval-workflows/_meta`, { withCredentials: true });
      setMeta({
        doc_types: Array.isArray(m.data?.doc_types) ? m.data.doc_types : [],
        approver_roles: Array.isArray(m.data?.approver_roles) ? m.data.approver_roles : [],
        departments: Array.isArray(m.data?.departments) ? m.data.departments : [],
        employees: Array.isArray(m.data?.employees) ? m.data.employees : [],
      });
    } catch (e) {
      toast.error('Failed to load approval workflow metadata');
    }
  };

  const loadWorkflows = async () => {
    setLoading(true);
    try {
      const deptParam = activeDept ? `&department_id=${activeDept}` : '&department_id=null';
      const w = await axios.get(`${API}/approval-workflows?doc_type=${activeDocType}${deptParam}`, { withCredentials: true });
      const list = Array.isArray(w.data) ? w.data : [];
      setWorkflows(list);
      const active = list.find(x => x.active);
      setLevels(Array.isArray(active?.levels)
        ? active.levels.map((l, i) => ({
            ...l,
            _key: `${i}-${l.name || 'Level'}`,
            name: l.name || '',
            approver_employee_ids: l.approver_employee_ids || [],
            approver_user_ids: l.approver_user_ids || [],
            approval_mode: l.approval_mode || 'any_one',
          }))
        : []);
    } catch (e) {
      setWorkflows([]); setLevels([]);
      toast.error('Load failed: ' + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  };

  const loadUsers = async () => {
    try {
      const u = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers((u.data || []).map(x => ({ id: x.id, name: x.name || x.email, email: x.email, role: x.role })));
    } catch (e) { /* optional */ }
  };

  const addLevel = () => {
    const nextOrder = levels.length + 1;
    setLevels([...levels, {
      _key: `new-${Date.now()}`,
      order: nextOrder,
      name: `Level ${nextOrder}`,
      approver_role: 'manager',
      approver_user_ids: [],
      approver_employee_ids: [],
      approval_mode: 'any_one',
      required: true,
      notes: '',
    }]);
  };

  const updateLevel = (idx, patch) => {
    setLevels(levels.map((l, i) => i === idx ? { ...l, ...patch } : l));
  };

  const removeLevel = (idx) => {
    setLevels(levels.filter((_, i) => i !== idx).map((l, i) => ({ ...l, order: i + 1 })));
  };

  const moveLevel = (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= levels.length) return;
    const copy = [...levels];
    [copy[idx], copy[ni]] = [copy[ni], copy[idx]];
    setLevels(copy.map((l, i) => ({ ...l, order: i + 1 })));
  };

  const save = async () => {
    if (levels.length === 0) {
      toast.error('Add at least one approval level before saving.');
      return;
    }
    for (let i = 0; i < levels.length; i++) {
      const l = levels[i];
      const trimmedName = (l.name || '').trim();
      if (!trimmedName) {
        toast.error(`Level name is required for level #${i + 1}.`);
        return;
      }
      const appType = l.approver_type || (l.approver_employee_ids?.length > 0 || l.approver_user_ids?.length > 0 ? 'employee' : (l.approver_role === 'manager' ? 'reporting_manager' : 'role'));
      const hasNamed = (l.approver_employee_ids?.length > 0) || (l.approver_user_ids?.length > 0);
      if (appType === 'employee' && !hasNamed) {
        toast.error(`Please select an employee for this approval level ('${trimmedName}').`);
        return;
      }
      if (!l.approver_role && !hasNamed) {
        toast.error(`Please select an employee for this approval level ('${trimmedName}').`);
        return;
      }
    }

    setSaving(true);
    try {
      const deptObj = meta.departments.find(d => strEquals(d.id, activeDept));
      const scopeName = deptObj ? `Dept-${deptObj.name}` : 'Default';

      await axios.post(`${API}/approval-workflows`, {
        doc_type: activeDocType,
        name: scopeName,
        department_id: activeDept || null,
        levels: levels.map((l, i) => ({
          order: i + 1,
          name: l.name.trim(),
          approver_type: l.approver_type || (l.approver_employee_ids?.length > 0 || l.approver_user_ids?.length > 0 ? 'employee' : (l.approver_role === 'manager' ? 'reporting_manager' : 'role')),
          approver_role: l.approver_role || null,
          approver_user_ids: l.approver_user_ids || [],
          approver_employee_ids: l.approver_employee_ids || [],
          approval_mode: l.approval_mode || 'any_one',
          required: l.required !== false,
          notes: (l.notes || '').trim(),
        })),
        active: true,
      }, { withCredentials: true });

      const scopeLabel = activeDept
        ? (deptObj?.name ? `${deptObj.name}${deptObj.code ? ` (${deptObj.code})` : ''}` : 'selected department')
        : 'All Departments (default fallback)';

      toast.success(`Approval Workflow saved successfully — applies to ${scopeLabel} for ${DOC_TYPE_LABEL[activeDocType]}.`);
      await loadWorkflows();
    } catch (e) {
      toast.error('Save failed: ' + (e?.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  // Bulk Upload Actions
  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const res = await axios.get(`${API}/approval-workflows/sample-template`, {
        responseType: 'blob',
        withCredentials: true,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Approval_Workflows_Bulk_Upload_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Sample template downloaded successfully.');
    } catch (e) {
      toast.error('Failed to download sample template.');
    }
    setDownloadingTemplate(false);
  };

  const handleExportWorkflows = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/approval-workflows/export`, {
        responseType: 'blob',
        withCredentials: true,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Approval_Workflows_Export_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Approval workflows exported successfully.');
    } catch (e) {
      toast.error('Failed to export approval workflows.');
    }
    setExporting(false);
  };

  const handleValidateFile = async () => {
    if (!uploadFile) {
      toast.error('Please select an Excel or CSV file to upload.');
      return;
    }
    setValidating(true);
    setValidationResult(null);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      const res = await axios.post(`${API}/approval-workflows/bulk-validate`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      setValidationResult(res.data);
      if (res.data.error_rows === 0) {
        toast.success(`Validation passed cleanly! ${res.data.valid_rows} rows ready for import.`);
      } else {
        toast.warning(`Validation completed with ${res.data.error_rows} error(s). Please review before importing.`);
      }
    } catch (e) {
      toast.error('Validation failed: ' + (e?.response?.data?.detail || e.message));
    }
    setValidating(false);
  };

  const handleConfirmImport = async () => {
    if (!validationResult || !validationResult.grouped_workflows?.length) {
      toast.error('No valid workflows available for import.');
      return;
    }
    setImporting(true);
    try {
      const res = await axios.post(`${API}/approval-workflows/bulk-import`, {
        grouped_workflows: validationResult.grouped_workflows,
        filename: uploadFile?.name || 'bulk_upload.xlsx',
      }, { withCredentials: true });

      toast.success(res.data.message || `Successfully imported ${res.data.workflows_processed} workflows.`);
      setUploadModalOpen(false);
      setUploadFile(null);
      setValidationResult(null);
      await loadWorkflows();
    } catch (e) {
      toast.error('Import failed: ' + (e?.response?.data?.detail || e.message));
    }
    setImporting(false);
  };

  function strEquals(a, b) {
    if (!a || !b) return false;
    return String(a) === String(b);
  }

  const activeDeptObj = useMemo(() => meta.departments.find(d => strEquals(d.id, activeDept)), [meta.departments, activeDept]);
  const selectedDeptLabel = activeDeptObj ? `${activeDeptObj.name}${activeDeptObj.code ? ` (${activeDeptObj.code})` : ''}` : 'All Departments (Default Fallback)';

  return (
    <MainLayout>
      <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="approval-workflows-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Workflow className="w-6 h-6 text-blue-600" />
              Approval Workflows Configuration
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Define multi-level, sequential approval chains per document type and department. Departments are mapped dynamically from Department Master.
            </p>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={handleDownloadTemplate}
              disabled={downloadingTemplate}
              className="border-slate-300 text-slate-700 hover:bg-slate-50 font-semibold shadow-2xs text-xs px-3 py-2"
              data-testid="wf-download-template-btn"
            >
              {downloadingTemplate ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Download className="w-3.5 h-3.5 mr-1.5 text-slate-600" />}
              Download Sample Template
            </Button>

            <Button
              variant="outline"
              onClick={handleExportWorkflows}
              disabled={exporting}
              className="border-slate-300 text-slate-700 hover:bg-slate-50 font-semibold shadow-2xs text-xs px-3 py-2"
              data-testid="wf-export-btn"
            >
              {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5 text-emerald-600" />}
              Export Workflows
            </Button>

            <Button
              variant="outline"
              onClick={() => { setUploadModalOpen(true); setUploadFile(null); setValidationResult(null); }}
              className="border-blue-300 text-blue-700 bg-blue-50/50 hover:bg-blue-100 font-semibold shadow-2xs text-xs px-3.5 py-2"
              data-testid="wf-bulk-upload-btn"
            >
              <Upload className="w-3.5 h-3.5 mr-1.5 text-blue-600" />
              Bulk Upload
            </Button>

            <Button
              onClick={save}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-2xs px-4 py-2 text-xs"
              data-testid="wf-save-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
              Save Workflow
            </Button>
          </div>
        </div>

        {/* Document Type Tabs */}
        <div className="flex flex-wrap gap-2 bg-slate-100 p-1.5 rounded-2xl border border-slate-200">
          {meta.doc_types.map(t => (
            <button
              key={t}
              onClick={() => setActiveDocType(t)}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                activeDocType === t
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-slate-700 hover:bg-slate-200/70 hover:text-slate-900'
              }`}
              data-testid={`wf-tab-${t}`}
            >
              {DOC_TYPE_LABEL[t] || t}
            </button>
          ))}
        </div>

        {/* Department Scope Selector */}
        <div className="bg-gradient-to-r from-blue-50/90 via-slate-50 to-indigo-50/80 border border-blue-200 rounded-2xl p-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="text-xs font-extrabold uppercase tracking-wider text-blue-900 shrink-0">
              Department Scope:
            </span>
            <select
              data-testid="wf-dept-select"
              value={activeDept}
              onChange={(e) => setActiveDept(e.target.value)}
              className="text-xs bg-white font-bold text-slate-900 border-2 border-blue-300 rounded-xl px-3 py-2 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-500/20 shadow-xs"
            >
              <option value="">All Departments (Default Fallback)</option>
              {meta.departments?.map(d => (
                <option key={d.id} value={d.id}>
                  {d.name}{d.code ? ` (${d.code})` : ''}
                </option>
              ))}
            </select>
          </div>
          <p className="text-xs text-blue-800 font-medium">
            {activeDept
              ? `→ Custom workflow for ${activeDeptObj?.name || 'selected department'}. Takes precedence over All Departments.`
              : '→ Default organization-wide workflow. Applies to all departments without a custom workflow.'}
          </p>
        </div>

        {/* Levels Chain Card */}
        <Card className="p-5 shadow-sm border border-slate-200 rounded-2xl bg-white space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <div>
              <h3 className="text-sm font-extrabold text-slate-900 flex items-center gap-2">
                Approval Chain — {DOC_TYPE_LABEL[activeDocType]} <span className="text-slate-500 font-normal">({selectedDeptLabel})</span>
                <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800 border border-blue-200">
                  {levels.length} Level{levels.length === 1 ? '' : 's'}
                </span>
              </h3>
            </div>
            <Button variant="outline" size="sm" onClick={addLevel} className="border-blue-300 text-blue-700 hover:bg-blue-50 font-semibold" data-testid="wf-add-level">
              <Plus className="w-4 h-4 mr-1 text-blue-600" /> Add Approval Level
            </Button>
          </div>

          {loading ? (
            <div className="py-12 text-center text-slate-500 flex flex-col items-center gap-2">
              <Loader2 className="w-7 h-7 animate-spin text-blue-600" />
              <span className="text-xs font-semibold">Loading workflow levels...</span>
            </div>
          ) : levels.length === 0 ? (
            <div className="py-12 text-center border-2 border-dashed border-slate-200 rounded-xl p-6 bg-slate-50/50">
              <Workflow className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-xs font-bold text-slate-700">No approval chain configured yet for {selectedDeptLabel}</p>
              <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
                Click "Add Approval Level" to configure your approvers (e.g. HOD, CFO, Director). If no custom chain is added, submissions route to the employee's Reporting Manager.
              </p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="wf-levels-list">
              {levels.map((lvl, idx) => (
                <LevelRow
                  key={lvl._key}
                  level={lvl}
                  index={idx}
                  total={levels.length}
                  meta={meta}
                  users={users}
                  onUpdate={(patch) => updateLevel(idx, patch)}
                  onRemove={() => removeLevel(idx)}
                  onMoveUp={() => moveLevel(idx, -1)}
                  onMoveDown={() => moveLevel(idx, +1)}
                />
              ))}
            </div>
          )}
        </Card>

        {/* Resolution Rules Summary Footer */}
        <div className="bg-slate-900 text-white rounded-2xl p-5 shadow-lg space-y-2 border border-slate-800">
          <p className="font-bold text-xs uppercase tracking-wider text-indigo-300 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-indigo-400" /> Approval Routing Priority Rules:
          </p>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-slate-300 font-medium">
            <li className="flex items-start gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
              <span><b>1. Department Scope</b>: Department-specific workflow applies first; falls back to All Departments default.</span>
            </li>
            <li className="flex items-start gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
              <span><b>2. Sequential Chain</b>: Level 1 must approve before Level 2 unlocks.</span>
            </li>
            <li className="flex items-start gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
              <span><b>3. Role or Named Approver</b>: Levels match designated roles (HOD, CFO, HR) or exact named employees.</span>
            </li>
            <li className="flex items-start gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
              <span><b>4. Manager Fallback</b>: Default fallback routes to submitting employee's Reporting Manager.</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Bulk Upload Modal */}
      {uploadModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col border border-slate-200 overflow-hidden animate-in fade-in duration-200">
            {/* Modal Header */}
            <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-blue-50/70 to-slate-50">
              <div>
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Upload className="w-5 h-5 text-blue-600" />
                  Bulk Upload Approval Workflows
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Upload an Excel or CSV file to configure multi-level workflows mapped to Department Master.
                </p>
              </div>
              <button onClick={() => setUploadModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1.5 rounded-full hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto space-y-6 flex-1">
              {/* File Select */}
              <div className="border-2 border-dashed border-blue-200 rounded-2xl p-6 text-center bg-blue-50/20 hover:bg-blue-50/50 transition-colors">
                <FileText className="w-10 h-10 text-blue-500 mx-auto mb-2" />
                <p className="text-xs font-bold text-slate-700">Select Excel (.xlsx) or CSV (.csv) file</p>
                <p className="text-[11px] text-slate-500 mt-1">Columns required: Document Type, Department, Level Order, Level Name, Approval Type (ROLE/EMPLOYEE), Role / Employee ID</p>
                
                <input
                  type="file"
                  id="wf-bulk-file-input"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => {
                    setUploadFile(e.target.files[0] || null);
                    setValidationResult(null);
                  }}
                  className="hidden"
                />

                <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                  <label htmlFor="wf-bulk-file-input" className="cursor-pointer bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-4 py-2 rounded-xl shadow-2xs flex items-center gap-2">
                    <Upload className="w-4 h-4 text-blue-600" />
                    {uploadFile ? uploadFile.name : 'Choose File'}
                  </label>

                  {uploadFile && (
                    <Button onClick={handleValidateFile} disabled={validating} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs px-4 py-2">
                      {validating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />}
                      Validate File
                    </Button>
                  )}
                </div>
              </div>

              {/* Validation Preview Results */}
              {validationResult && (
                <div className="space-y-4">
                  {/* Summary Metric Cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-center">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Total Rows</span>
                      <span className="text-lg font-black text-slate-900">{validationResult.total_rows}</span>
                    </div>

                    <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-center">
                      <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider block">Valid Rows</span>
                      <span className="text-lg font-black text-emerald-700">{validationResult.valid_rows}</span>
                    </div>

                    <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-center">
                      <span className="text-[10px] font-bold text-rose-700 uppercase tracking-wider block">Errors</span>
                      <span className="text-lg font-black text-rose-700">{validationResult.error_rows}</span>
                    </div>

                    <div className="p-3 rounded-xl bg-blue-50 border border-blue-200 text-center">
                      <span className="text-[10px] font-bold text-blue-700 uppercase tracking-wider block">Workflows To Save</span>
                      <span className="text-lg font-black text-blue-700">{validationResult.workflows_count}</span>
                    </div>
                  </div>

                  {/* Departments Detected */}
                  {validationResult.departments_detected?.length > 0 && (
                    <div className="text-xs bg-slate-50 p-3 rounded-xl border border-slate-200 flex items-center gap-2">
                      <span className="font-bold text-slate-700">Departments Detected ({validationResult.departments_detected.length}):</span>
                      <span className="text-slate-600 font-medium">{validationResult.departments_detected.join(', ')}</span>
                    </div>
                  )}

                  {/* Preview Table */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden max-h-64 overflow-y-auto">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead>
                        <tr className="bg-slate-100 text-slate-700 font-bold border-b border-slate-200 sticky top-0 z-10">
                          <th className="p-2.5">Row</th>
                          <th className="p-2.5">Document Type</th>
                          <th className="p-2.5">Department</th>
                          <th className="p-2.5 text-center">Level</th>
                          <th className="p-2.5">Level Name</th>
                          <th className="p-2.5">Approver</th>
                          <th className="p-2.5">Status</th>
                          <th className="p-2.5">Details / Errors</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {validationResult.rows?.map((r) => (
                          <tr key={r.row_index} className={r.status === 'VALID' ? 'hover:bg-slate-50' : 'bg-rose-50/30 hover:bg-rose-50/50'}>
                            <td className="p-2.5 font-mono text-slate-500 font-bold">{r.row_index}</td>
                            <td className="p-2.5 font-semibold text-slate-900">{r.doc_type_label}</td>
                            <td className="p-2.5 font-medium text-slate-700">
                              {r.department_name} {r.department_code && <span className="text-slate-400 font-mono text-[10px]">({r.department_code})</span>}
                            </td>
                            <td className="p-2.5 text-center font-bold text-slate-700">#{r.level_order}</td>
                            <td className="p-2.5 font-semibold text-slate-800">{r.level_name}</td>
                            <td className="p-2.5">
                              {r.approval_type === 'ROLE' ? (
                                <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md font-semibold text-[11px] border border-slate-200">
                                  Role: {r.role_label || r.role}
                                </span>
                              ) : (
                                <span className="bg-indigo-100 text-indigo-900 px-2 py-0.5 rounded-md font-semibold text-[11px] border border-indigo-200">
                                  Emp: {r.employee_name || r.employee_id}
                                </span>
                              )}
                            </td>
                            <td className="p-2.5">
                              <span className={`px-2 py-0.5 rounded-full font-extrabold text-[10px] uppercase border ${
                                r.status === 'VALID'
                                  ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                                  : 'bg-rose-100 text-rose-800 border-rose-200'
                              }`}>
                                {r.status}
                              </span>
                            </td>
                            <td className="p-2.5 text-rose-700 font-medium text-[11px]">
                              {r.error_message || <span className="text-emerald-600 font-semibold">✓ Valid</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
              <Button variant="ghost" onClick={() => setUploadModalOpen(false)} className="text-slate-600 font-semibold">
                Cancel
              </Button>

              <div className="flex items-center gap-2">
                {validationResult?.error_rows > 0 && (
                  <span className="text-xs text-rose-600 font-bold flex items-center gap-1 mr-2">
                    <AlertTriangle className="w-4 h-4" /> Please fix file errors before importing.
                  </span>
                )}

                <Button
                  onClick={handleConfirmImport}
                  disabled={importing || !validationResult || validationResult.valid_rows === 0 || validationResult.error_rows > 0}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-xs px-5 text-xs"
                  data-testid="wf-confirm-import-btn"
                >
                  {importing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                  Confirm & Import ({validationResult?.valid_rows || 0} rows)
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}

function LevelRow({ level, index, total, meta, users, onUpdate, onRemove, onMoveUp, onMoveDown }) {
  const [empQuery, setEmpQuery] = useState('');

  // Determine current approver type
  const approverType = useMemo(() => {
    if (level.approver_type) return level.approver_type;
    if (level.approver_employee_ids?.length > 0 || level.approver_user_ids?.length > 0) return 'employee';
    if (level.approver_role === 'manager') return 'reporting_manager';
    if (level.approver_role) return 'role';
    return 'reporting_manager';
  }, [level.approver_type, level.approver_employee_ids, level.approver_user_ids, level.approver_role]);

  // Search pool combining employees and users
  const searchPool = useMemo(() => {
    const list = [];
    const seenIds = new Set();

    (meta.employees || []).forEach(emp => {
      const idKey = emp.employee_id || emp.id;
      if (!idKey || seenIds.has(idKey)) return;
      seenIds.add(idKey);
      list.push({
        id: idKey,
        employee_id: idKey,
        user_id: emp.user_id || '',
        name: emp.name,
        code: emp.employee_code,
        email: emp.email,
        dept: emp.department_name,
        designation: emp.designation,
        type: 'employee',
      });
    });

    (users || []).forEach(u => {
      const uKey = u.id;
      if (!uKey || seenIds.has(uKey)) return;
      seenIds.add(uKey);
      list.push({
        id: uKey,
        employee_id: '',
        user_id: uKey,
        name: u.name,
        code: '',
        email: u.email,
        dept: '',
        designation: u.role,
        type: 'user',
      });
    });

    return list;
  }, [meta.employees, users]);

  // Selected items
  const selectedItems = useMemo(() => {
    const empIds = level.approver_employee_ids || [];
    const userIds = level.approver_user_ids || [];
    return searchPool.filter(x => (x.employee_id && empIds.includes(x.employee_id)) || (x.user_id && userIds.includes(x.user_id)));
  }, [searchPool, level.approver_employee_ids, level.approver_user_ids]);

  // Filter matched search options
  const matchedOptions = useMemo(() => {
    if (!empQuery.trim()) return [];
    const q = empQuery.toLowerCase();
    const selEmpIds = level.approver_employee_ids || [];
    const selUserIds = level.approver_user_ids || [];

    return searchPool.filter(x => {
      if (x.employee_id && selEmpIds.includes(x.employee_id)) return false;
      if (x.user_id && selUserIds.includes(x.user_id)) return false;
      return (
        x.name?.toLowerCase().includes(q) ||
        x.code?.toLowerCase().includes(q) ||
        x.email?.toLowerCase().includes(q) ||
        x.dept?.toLowerCase().includes(q)
      );
    }).slice(0, 8);
  }, [searchPool, empQuery, level.approver_employee_ids, level.approver_user_ids]);

  const handleApproverTypeChange = (newType) => {
    if (newType === 'reporting_manager') {
      onUpdate({
        approver_type: 'reporting_manager',
        approver_role: 'manager',
        approver_employee_ids: [],
        approver_user_ids: [],
      });
    } else if (newType === 'role') {
      onUpdate({
        approver_type: 'role',
        approver_role: level.approver_role || 'hr_admin',
        approver_employee_ids: [],
        approver_user_ids: [],
      });
    } else if (newType === 'employee') {
      onUpdate({
        approver_type: 'employee',
        approver_role: null,
      });
    }
  };

  const addApproverItem = (item) => {
    const empIds = [...(level.approver_employee_ids || [])];
    const userIds = [...(level.approver_user_ids || [])];

    if (item.employee_id && !empIds.includes(item.employee_id)) {
      empIds.push(item.employee_id);
    }
    if (item.user_id && !userIds.includes(item.user_id)) {
      userIds.push(item.user_id);
    }

    onUpdate({
      approver_type: 'employee',
      approver_role: null,
      approver_employee_ids: empIds,
      approver_user_ids: userIds,
    });
    setEmpQuery('');
  };

  const removeApproverItem = (item) => {
    const empIds = (level.approver_employee_ids || []).filter(id => id !== item.employee_id);
    const userIds = (level.approver_user_ids || []).filter(id => id !== item.user_id);
    onUpdate({
      approver_employee_ids: empIds,
      approver_user_ids: userIds,
    });
  };

  const priorityBadge = approverType === 'employee'
    ? { label: 'Mapped Employee Approver', cls: 'bg-indigo-100 text-indigo-800 border-indigo-200' }
    : approverType === 'role'
    ? { label: 'Role-Based Match', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' }
    : { label: 'Reporting Manager Match', cls: 'bg-amber-100 text-amber-800 border-amber-200' };

  const isMissingEmployee = approverType === 'employee' && selectedItems.length === 0;

  return (
    <div className={`border rounded-xl p-4 bg-white shadow-xs transition-colors ${
      isMissingEmployee ? 'border-rose-400 bg-rose-50/20 ring-1 ring-rose-300' : 'border-slate-200 hover:border-slate-300'
    }`} data-testid={`wf-level-row-${index}`}>
      <div className="flex items-start gap-3">
        <div className="flex flex-col items-center pt-1">
          <button onClick={onMoveUp} disabled={index === 0} className="text-slate-400 hover:text-blue-600 disabled:opacity-20 p-0.5">
            <ArrowUp className="w-3.5 h-3.5" />
          </button>
          <span className="font-mono text-xs font-extrabold text-slate-700 my-1 bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200">
            #{index + 1}
          </span>
          <button onClick={onMoveDown} disabled={index === total - 1} className="text-slate-400 hover:text-blue-600 disabled:opacity-20 p-0.5">
            <ArrowDown className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="flex-1 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border uppercase tracking-wider ${priorityBadge.cls}`}>
              {priorityBadge.label}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-[11px] font-bold text-slate-700">Level Name *</label>
              <input
                value={level.name || ''}
                onChange={e => onUpdate({ name: e.target.value })}
                placeholder="e.g. Reporting Manager / Finance Approval / Final Approval"
                className="w-full border border-slate-300 rounded-xl px-3 py-1.5 text-xs font-semibold mt-1 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                data-testid={`wf-level-name-${index}`}
              />
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-700">Approver Type *</label>
              <select
                value={approverType}
                onChange={e => handleApproverTypeChange(e.target.value)}
                className="w-full border border-slate-300 rounded-xl px-3 py-1.5 text-xs font-semibold mt-1 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                data-testid={`wf-level-approver-type-${index}`}
              >
                <option value="reporting_manager">Reporting Manager</option>
                <option value="employee">Specific Employee (Mapped by Code)</option>
                <option value="role">Match By Role</option>
              </select>
            </div>

            {approverType === 'role' && (
              <div>
                <label className="text-[11px] font-bold text-slate-700">Approver Role *</label>
                <select
                  value={level.approver_role || 'hr_admin'}
                  onChange={e => onUpdate({ approver_role: e.target.value || 'hr_admin' })}
                  className="w-full border border-slate-300 rounded-xl px-3 py-1.5 text-xs font-semibold mt-1 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                  data-testid={`wf-level-role-${index}`}
                >
                  {meta.approver_roles?.map(r => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}
                </select>
              </div>
            )}

            {approverType === 'employee' && selectedItems.length > 1 && (
              <div>
                <label className="text-[11px] font-bold text-slate-700">Multi-Approver Mode</label>
                <select
                  value={level.approval_mode || 'any_one'}
                  onChange={e => onUpdate({ approval_mode: e.target.value })}
                  className="w-full border border-slate-300 rounded-xl px-3 py-1.5 text-xs font-semibold mt-1 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                >
                  <option value="any_one">Any one approver can approve</option>
                  <option value="all">All approvers must approve</option>
                </select>
              </div>
            )}
          </div>

          {/* Named Employee Mapping Control */}
          {approverType === 'employee' && (
            <div className="relative space-y-2">
              <label className="text-[11px] font-bold text-slate-700 flex items-center justify-between">
                <span className="flex items-center gap-1">
                  <Search className="w-3.5 h-3.5 text-blue-600" />
                  Map Employee (Search Employee Code or Name) *
                </span>
                <span className="text-[10px] text-slate-500">Resolved from Canonical Employee Master</span>
              </label>

              {/* Mapped Employee Info Cards */}
              {selectedItems.length > 0 && (
                <div className="space-y-2">
                  {selectedItems.map(item => (
                    <div key={item.id} className="p-3 bg-indigo-50/80 border border-indigo-200 rounded-xl flex items-center justify-between shadow-2xs">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <span className="font-extrabold text-xs text-indigo-950">{item.name}</span>
                          {item.code && (
                            <span className="font-mono text-[11px] font-bold bg-indigo-200/80 text-indigo-900 px-2 py-0.5 rounded-md border border-indigo-300">
                              {item.code}
                            </span>
                          )}
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
                            Active
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-600 font-medium flex flex-wrap items-center gap-x-3 gap-y-0.5">
                          {item.dept && <span>Dept: <b>{item.dept}</b></span>}
                          {item.designation && <span>Desig: <b>{item.designation}</b></span>}
                          {item.email && <span>Email: <b>{item.email}</b></span>}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeApproverItem(item)}
                        className="text-slate-400 hover:text-rose-600 font-bold p-1 hover:bg-white rounded-lg transition-colors"
                        title="Remove mapped employee"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Employee Code Search Input */}
              <div className="relative">
                <div className="border border-slate-300 rounded-xl p-2 min-h-[38px] flex items-center gap-2 bg-slate-50/50 focus-within:bg-white focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20">
                  <Search className="w-3.5 h-3.5 text-slate-400 shrink-0 ml-1" />
                  <input
                    value={empQuery}
                    onChange={e => setEmpQuery(e.target.value)}
                    placeholder={selectedItems.length === 0 ? "Search Employee Code (e.g. S2035) or Name…" : "Add another employee by code or name…"}
                    className="flex-1 outline-none text-xs bg-transparent font-semibold text-slate-900 placeholder:text-slate-400"
                    data-testid={`wf-level-user-search-${index}`}
                  />
                </div>

                {empQuery && matchedOptions.length > 0 && (
                  <div className="absolute z-20 bg-white border border-slate-200 rounded-xl shadow-xl mt-1 max-h-48 overflow-y-auto w-full left-0 divide-y divide-slate-100">
                    {matchedOptions.map(opt => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => addApproverItem(opt)}
                        className="block w-full text-left px-3 py-2 text-xs hover:bg-blue-50 transition-colors"
                        data-testid={`wf-level-user-opt-${index}-${opt.id}`}
                      >
                        <div className="font-bold text-slate-900 flex items-center gap-2">
                          <span>{opt.name}</span>
                          {opt.code && <span className="font-mono text-blue-600 font-semibold">({opt.code})</span>}
                        </div>
                        <div className="text-[10px] text-slate-500 font-medium">
                          {opt.dept && `${opt.dept} • `}{opt.designation && `${opt.designation} • `}{opt.email}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {isMissingEmployee && (
                <p className="text-xs font-bold text-rose-600 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> Please select an employee for this approval level.
                </p>
              )}
            </div>
          )}
        </div>

        <button onClick={onRemove} className="text-slate-400 hover:text-rose-600 p-1 mt-1 transition-colors" data-testid={`wf-level-remove-${index}`} title="Remove Level">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <input
        value={level.notes || ''}
        onChange={e => onUpdate({ notes: e.target.value })}
        placeholder="Optional instructions or notes for this approval level..."
        className="w-full border border-slate-200 rounded-xl px-3 py-1.5 text-xs mt-3 text-slate-600 bg-slate-50/50 font-medium"
      />
    </div>
  );
}
